/*
  Memory page — /memory. Natural-language search + unified timeline across
  notes, inbox, todos, shopping, prescriptions, routines, subscriptions.
*/
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, Sparkles, StickyNote, Inbox, CheckSquare, ShoppingCart,
  Pill, Layers, Receipt, ArrowUpRight, Loader2, History,
} from "lucide-react";
import { phase4Api } from "../lib/phase4Api";

const SOURCE_META = {
  note:         { label: "Note",        icon: StickyNote,   color: "var(--primary-soft)",  ink: "var(--primary-ink)" },
  inbox:        { label: "Inbox",       icon: Inbox,        color: "var(--warning-bg)",    ink: "var(--warning)" },
  todo:         { label: "To-Do",       icon: CheckSquare,  color: "var(--warning-bg)",    ink: "var(--warning)" },
  shopping:     { label: "Shopping",    icon: ShoppingCart, color: "var(--accent-soft)",   ink: "var(--accent-ink)" },
  prescription: { label: "Prescription", icon: Pill,        color: "var(--primary-soft)",  ink: "var(--primary-ink)" },
  routine:      { label: "Routine",     icon: Layers,       color: "var(--primary-soft)",  ink: "var(--primary-ink)" },
  subscription: { label: "Subscription", icon: Receipt,     color: "var(--accent-soft)",   ink: "var(--accent-ink)" },
};

const FILTERS = [
  ["all", "All"],
  ["note", "Notes"],
  ["inbox", "Inbox"],
  ["todo", "To-Dos"],
  ["shopping", "Shopping"],
  ["prescription", "Rx"],
  ["routine", "Routines"],
  ["subscription", "Subs"],
];

function MemoryRow({ item }) {
  const meta = SOURCE_META[item.source] || SOURCE_META.note;
  const Icon = meta.icon;
  const Wrapper = item.link ? Link : "div";
  const wrapperProps = item.link ? { to: item.link } : {};
  return (
    <Wrapper
      {...wrapperProps}
      data-testid={`memory-row-${item.source}-${item.id}`}
      className="card-tactile rounded-xl p-4 flex items-start gap-3 hover:translate-y-[-1px] transition-transform group"
    >
      <div
        className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0"
        style={{ background: meta.color }}
      >
        <Icon className="h-4 w-4" style={{ color: meta.ink }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <span
            className="text-[10px] uppercase tracking-wider font-bold"
            style={{ color: meta.ink }}
          >
            {meta.label}
          </span>
          <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>
            {(item.created_at || "").slice(0, 10)}
          </span>
        </div>
        <div className="font-semibold text-[color:var(--text-main)] truncate">
          {item.title}
        </div>
        {item.excerpt && (
          <p className="text-sm text-[color:var(--text-muted)] mt-0.5 line-clamp-2">
            {item.excerpt}
          </p>
        )}
      </div>
      {item.link && (
        <ArrowUpRight
          className="h-4 w-4 shrink-0 opacity-50 group-hover:opacity-100"
          style={{ color: "var(--text-muted)" }}
        />
      )}
    </Wrapper>
  );
}

export default function MemoryPage() {
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [loadingTimeline, setLoadingTimeline] = useState(true);
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResult, setSearchResult] = useState(null);

  async function loadTimeline() {
    setLoadingTimeline(true);
    try {
      const data = await phase4Api.timeline({
        source: filter === "all" ? undefined : filter,
        limit: 60,
      });
      setItems(data);
    } catch {
      toast.error("Couldn't load timeline");
    } finally {
      setLoadingTimeline(false);
    }
  }

  useEffect(() => {
    loadTimeline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  async function runSearch(e) {
    e?.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    setSearchResult(null);
    try {
      const r = await phase4Api.search(query.trim());
      setSearchResult(r);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Search failed");
    } finally {
      setSearching(false);
    }
  }

  function clearSearch() {
    setQuery("");
    setSearchResult(null);
  }

  const counts = useMemo(() => {
    const c = {};
    for (const it of items) c[it.source] = (c[it.source] || 0) + 1;
    return c;
  }, [items]);

  return (
    <div className="animate-fade-up" data-testid="memory-page">
      <section className="mb-8">
        <span className="label-caps">Contextual Memory · AI-powered recall</span>
        <h1 className="mt-2 text-4xl sm:text-5xl font-display font-black tracking-tighter">
          Everything you've captured, searchable.
        </h1>
        <p className="mt-3 max-w-2xl text-[color:var(--text-muted)] leading-relaxed">
          Ask a question about your own data — notes, todos, shopping, routines — and
          Gemini will surface the relevant moments.
        </p>
      </section>

      {/* Search bar */}
      <section className="mb-8">
        <form onSubmit={runSearch} className="card-tactile rounded-2xl p-4 sm:p-5">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 shrink-0" style={{ color: "var(--primary-ink)" }} />
            <input
              data-testid="memory-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Show me my ideas about nutrition…"
              className="flex-1 bg-transparent outline-none text-base text-[color:var(--text-main)] placeholder:text-[color:var(--text-muted)] min-w-0"
            />
            {query && (
              <button
                type="button"
                onClick={clearSearch}
                data-testid="memory-search-clear"
                className="text-xs font-semibold px-2 py-1 rounded hover:bg-[color:var(--surface-2)]"
                style={{ color: "var(--text-muted)" }}
              >
                Clear
              </button>
            )}
            <button
              type="submit"
              disabled={searching || !query.trim()}
              data-testid="memory-search-submit"
              className="inline-flex items-center gap-1.5 px-4 h-10 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
              style={{ background: "var(--primary)" }}
            >
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              {searching ? "Searching…" : "Ask"}
            </button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {[
              "What did I note about marketing?",
              "Show my shopping patterns",
              "Ideas I've saved this month",
            ].map((ex) => (
              <button
                key={ex}
                type="button"
                onClick={() => setQuery(ex)}
                data-testid={`memory-search-example-${ex.slice(0, 10).replace(/\s+/g, "-")}`}
                className="text-xs px-3 py-1 rounded-full border hover:bg-[color:var(--surface-2)]"
                style={{ borderColor: "var(--border-subtle)", color: "var(--text-muted)" }}
              >
                {ex}
              </button>
            ))}
          </div>
        </form>

        {searchResult && (
          <div
            data-testid="memory-search-result"
            className="mt-4 card-tactile rounded-2xl p-5 border-l-4"
            style={{ borderLeftColor: "var(--primary)" }}
          >
            <div className="flex items-start gap-3">
              <div className="h-8 w-8 rounded-lg bg-[color:var(--primary-soft)] flex items-center justify-center shrink-0">
                <Sparkles className="h-4 w-4" style={{ color: "var(--primary-ink)" }} />
              </div>
              <div className="flex-1">
                <div className="label-caps mb-1">Answer</div>
                <p className="text-base leading-relaxed text-[color:var(--text-main)]">
                  {searchResult.answer}
                </p>
              </div>
            </div>
            {searchResult.sources?.length > 0 && (
              <div className="mt-4">
                <div className="label-caps mb-2">Sources ({searchResult.sources.length})</div>
                <div className="space-y-2">
                  {searchResult.sources.map((it) => (
                    <MemoryRow key={`${it.source}-${it.id}`} item={it} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Filter chips */}
      <section className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <History className="h-4 w-4" style={{ color: "var(--text-muted)" }} />
          <span className="label-caps">Timeline</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {FILTERS.map(([key, label]) => {
            const active = filter === key;
            const badge = key !== "all" && counts[key];
            return (
              <button
                key={key}
                onClick={() => setFilter(key)}
                data-testid={`memory-filter-${key}`}
                className="text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors"
                style={{
                  background: active ? "var(--primary-soft)" : "var(--surface)",
                  borderColor: active ? "var(--primary)" : "var(--border-subtle)",
                  color: active ? "var(--primary-ink)" : "var(--text-muted)",
                }}
              >
                {label}
                {badge ? (
                  <span className="ml-1.5 opacity-70">· {badge}</span>
                ) : null}
              </button>
            );
          })}
        </div>
      </section>

      {/* Timeline */}
      <section>
        {loadingTimeline ? (
          <div className="card-tactile rounded-xl p-8 text-center" style={{ color: "var(--text-muted)" }}>
            <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
            Loading your timeline…
          </div>
        ) : items.length === 0 ? (
          <div className="card-tactile rounded-xl p-10 text-center" data-testid="memory-empty">
            <History className="h-8 w-8 mx-auto mb-3" style={{ color: "var(--text-muted)" }} />
            <h3 className="font-display font-bold text-lg mb-1">No memory yet</h3>
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>
              Start capturing — the Life Inbox, Notes, and Todos all feed this page.
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="memory-timeline">
            {items.map((it) => (
              <MemoryRow key={`${it.source}-${it.id}`} item={it} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
